import { CardContainer, CardKey } from '../../StyledComponents/Card';
import CondorSpinner from '../CondorSpinner';
import { IconButton } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getDeviceTags } from '../../services/DevicesService';

interface CondorStandardTagsProps {
  purpleTag: {
    tagId: string;
    tagName: string;
  };
  yellowTag: {
    tagId: string;
    tagName: string;
  };
}

const PAGE_SIZE = 1000;

const fetchAllDeviceTags = async (tagId: string) => {
  let allResults: any[] = [];
  let next: string | undefined = undefined;

  do {
    const data = await getDeviceTags({
      tagId,
      limit: PAGE_SIZE,
      nextToken: next,
    });
    allResults = [...allResults, ...(data?.results ?? [])];
    next = data?.next;
  } while (next);

  return { results: allResults, count: allResults.length };
};

export const CondorStandardTags = (props: CondorStandardTagsProps) => {
  const { purpleTag, yellowTag } = props;

  const purpleEnabled = !!purpleTag.tagId;
  const yellowEnabled = !!yellowTag.tagId;

  const { data: purpleData, isPending: purplePending } = useQuery({
    queryKey: ['condor-standard-tags-purple', purpleTag.tagId],
    queryFn: () => fetchAllDeviceTags(purpleTag.tagId),
    enabled: purpleEnabled,
  });

  const { data: yellowData, isPending: yellowPending } = useQuery({
    queryKey: ['condor-standard-tags-yellow', yellowTag.tagId],
    queryFn: () => fetchAllDeviceTags(yellowTag.tagId),
    enabled: yellowEnabled,
  });

  const isLoading =
    (purpleEnabled && purplePending) || (yellowEnabled && yellowPending);

  const purpleDeviceCount = purpleData?.results?.length ?? 0;
  const yellowDeviceCount = yellowData?.results?.length ?? 0;
  const totalDevices = purpleDeviceCount + yellowDeviceCount;

  const purplePercentage =
    totalDevices > 0
      ? ((purpleDeviceCount / totalDevices) * 100).toFixed(1)
      : '0.0';

  const yellowPercentage =
    totalDevices > 0
      ? ((yellowDeviceCount / totalDevices) * 100).toFixed(1)
      : '0.0';

  const handleDownload = (
    tag: { tagId: string; tagName: string },
    data: any,
  ) => {
    const serialNumbers = data?.results
      ?.map((dt: any) => dt?.serialNumber)
      .filter((sn: any): sn is string => !!sn);

    if (!serialNumbers || serialNumbers.length === 0) {
      console.error('No serial numbers found for tagId: ' + tag.tagId);
      return;
    }

    const csvContent = ['Serial Number', ...serialNumbers].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${tag.tagName}-serial-numbers.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ flexBasis: '20%' }}></div>
      <div style={{ flexBasis: '60%' }}>
        <CardContainer>
          {isLoading ? (
            <CondorSpinner />
          ) : (
            <div>
              <div
                style={{
                  display: 'flex',
                  marginBottom: '16px',
                  alignItems: 'center',
                }}
              >
                <div style={{ flexBasis: '25%' }}>
                  <CardKey>Purple Tag</CardKey>
                </div>
                <div style={{ flexBasis: '25%', fontSize: '1rem' }}>
                  {purpleTag.tagName}
                </div>
                <div
                  style={{
                    flexBasis: '35%',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: '#6b46c1',
                  }}
                >
                  {purpleDeviceCount} devices ({purplePercentage}%)
                </div>
                <div style={{ flexBasis: '15%', textAlign: 'right' }}>
                  <IconButton
                    onClick={() => handleDownload(purpleTag, purpleData)}
                    aria-label="download-purple-tag-serial-numbers"
                    title="Download Purple Tag Serial Numbers"
                    disabled={!purpleData?.results?.length}
                  >
                    <Download />
                  </IconButton>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flexBasis: '25%' }}>
                  <CardKey>Yellow Tag</CardKey>
                </div>
                <div style={{ flexBasis: '25%', fontSize: '1rem' }}>
                  {yellowTag.tagName}
                </div>
                <div
                  style={{
                    flexBasis: '35%',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: '#d69e2e',
                  }}
                >
                  {yellowDeviceCount} devices ({yellowPercentage}%)
                </div>
                <div style={{ flexBasis: '15%', textAlign: 'right' }}>
                  <IconButton
                    onClick={() => handleDownload(yellowTag, yellowData)}
                    aria-label="download-yellow-tag-serial-numbers"
                    title="Download Yellow Tag Serial Numbers"
                    disabled={!yellowData?.results?.length}
                  >
                    <Download />
                  </IconButton>
                </div>
              </div>
            </div>
          )}
        </CardContainer>
      </div>
      <div style={{ flexBasis: '20%' }}></div>
    </div>
  );
};
